// src/modules/escalante/EscalanteDashboard.js
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { db, paths } from '../../firebase/config';
import {
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  collection,
  query,
  where,
  getDocs,
  runTransaction,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from "firebase/firestore";
import { useAuth, logAuditoria } from '../../context/AuthContext';
import { LoadingSpinner, ModalLoadingSpinner } from '../../components/LoadingSpinner';
import { ChangePasswordModal } from '../../components/ChangePasswordModal';
import { Modal } from '../../components/Modal';
import { Button } from '../../components/Button';
import { OpcaoCard } from '../../components/OpcaoCard';
import { PrintDateModal } from '../../components/PrintDateModal';
import { CancelamentoEscolhaModal } from '../../components/CancelamentoEscolhaModal';
import { AlocarCadeteExternoModal } from '../../components/AlocarCadeteExternoModal'; 
import {
  getCPAName,
  getNomeCompleto,
  limparCPF,
  formatarData,
  getDiaDaSemana,
  getNormalizedDateString
} from '../../utils/helpers';

// --- FUNÇÕES DE LIMPEZA E ORDENAÇÃO DE SEGURANÇA ---
const limparNumero = (val) => {
    if (val === undefined || val === null || val === '') return '';
    if (typeof val === 'number') return val;
    if (typeof val === 'string') {
        const apenasNumeros = val.replace(/\D/g, '');
        if (apenasNumeros !== '') return parseInt(apenasNumeros, 10);
    }
    return '';
};

const limparNomeParaSort = (nome) => {
    if (!nome) return '';
    return nome.replace(/^(Al Of PM|Cad PM|Cadete|Aluno Oficial|Escalante)\s+/i, '').trim().toUpperCase();
};

const extrairNumero = (c) => {
    const chavesParaBuscar = [
        'ordem_antiguidade', 'ordemAntiguidade', 'ordem', 'Ordem', 'ORDEM',
        'numero', 'Numero', 'NUMERO', 'numero_chamada', 'numeroChamada',
        'classificacao', 'Classificacao', 'antiguidade', 'Antiguidade'
    ];
    for (let chave of chavesParaBuscar) {
        if (c[chave] !== undefined && c[chave] !== null && c[chave] !== '') {
            const numLimpo = limparNumero(c[chave]);
            if (numLimpo !== '') return numLimpo;
        }
    }
    return ''; 
};

// --- ÍCONES ---
const QueueListIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg>);
const UserGroupIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>);
const CalendarIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 00-2-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>);
const PencilIcon = () => (<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>);
const KeyIcon = () => (<svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2v5a2 2 0 01-2 2h-5a2 2 0 01-2-2V9a2 2 0 012-2h5zM7 9a2 2 0 012-2h.01M7 13a2 2 0 012-2h.01M7 17a2 2 0 012-2h.01M17 17v2a2 2 0 01-2 2H5a2 2 0 01-2-2V9a2 2 0 012-2h2L7 9m0 0l-2 2" /></svg>);
const UserActiveIcon = ({ className = "w-5 h-5" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>);
const UserSkippedIcon = ({ className = "w-5 h-5" }) => (<svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>);
const EditPencilIcon = () => (<svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>);

export const EscalanteDashboard = () => {
  const { userProfile } = useAuth();

  // --- Estados de Dados (Tempo Real) ---
  const [pelotaoData, setPelotaoData] = useState(null); 
  const [cadetes, setCadetes] = useState([]);
  const [escolhas, setEscolhas] = useState([]); 
  const [locais, setLocais] = useState([]);
  const [datasEstagio, setDatasEstagio] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allPelotoes, setAllPelotoes] = useState([]); 

  // --- Estados de Interface e Modais ---
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleteAction, setDeleteAction] = useState(null);
  const [modalView, setModalView] = useState(''); 
  const [currentItem, setCurrentItem] = useState(null); 
  const [formData, setFormData] = useState({});
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(''); 
  const [globalError, setGlobalError] = useState(''); 

  const [activeTab, setActiveTab] = useState('fila');
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

  // --- Estados de Escolha ---
  const [todasEscolhas, setTodasEscolhas] = useState([]);
  const [meuHistorico, setMeuHistorico] = useState([]);
  const [showModalEscolha, setShowModalEscolha] = useState(false);
  const [showModalImpossibilidade, setShowModalImpossibilidade] = useState(false);
  const [motivoImpossibilidade, setMotivoImpossibilidade] = useState('');
  const [selectedEstagio, setSelectedEstagio] = useState(null);
  const [selectedDateToSkip, setSelectedDateToSkip] = useState(null);
  const [deleteBulkDate, setDeleteBulkDate] = useState('');

  // --- NOVO: Estado para Modal de Gerenciamento de Escolha ---
  const [showGerenciarEscolhaModal, setShowGerenciarEscolhaModal] = useState(false);
  const [escolhaEmEdicao, setEscolhaEmEdicao] = useState(null);
  const [novaVagaId, setNovaVagaId] = useState('');

  const [showModalCancelamento, setShowModalCancelamento] = useState(false);
  const [escolhaParaCancelar, setEscolhaParaCancelar] = useState(null);
  
  const [cadetesExternos, setCadetesExternos] = useState([]); 
  const [showPrintDateModal, setShowPrintDateModal] = useState(false);
  const [showAlocarExternoModal, setShowAlocarExternoModal] = useState(false);

  const [filterDate, setFilterDate] = useState(''); 
  const [filterLocalId, setFilterLocalId] = useState('');

  // --- ESTADO: Controle do Acordeon ---
  const [expandedCadetes, setExpandedCadetes] = useState({});

  const toggleCadeteAccordion = (id) => {
      setExpandedCadetes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const pelotaoId = userProfile.pelotao_id;

  // --------------------------------------------------------------------------
  // CARREGAMENTO DE DADOS (USE EFFECTS - OTIMIZADOS)
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!pelotaoId) {
        setLoading(false);
        return;
    }
    setLoading(true);

    // --- DADOS EM TEMPO REAL (Necessários para a fila funcionar ao vivo) ---
    const unsubPelotaoData = onSnapshot(doc(db, paths.pelotoes, pelotaoId), (docSnap) => {
        if (docSnap.exists()) {
            setPelotaoData({ id: docSnap.id, ...docSnap.data() });
            if (activeTab === 'assinaturas') {
                setFormData(prev => ({ 
                    ...prev, 
                    nome_escalante: docSnap.data().nome_escalante || getNomeCompleto(userProfile.nome),
                    posto_escalante: docSnap.data().posto_escalante || 'Cad PM',
                    nome_cmt_pelotao: docSnap.data().nome_cmt_pelotao || '',
                    posto_cmt_pelotao: docSnap.data().posto_cmt_pelotao || '1º Ten PM',
                }));
            }
        }
    }, (err) => console.error("Erro (Pelotão Data RT):", err));

    const qCadetes = query(collection(db, paths.users),
      where("pelotao_id", "==", pelotaoId),
      where("nivel", "in", ["cadete", "escalante", "cadete_academico"])
    );
    const unsubCadetes = onSnapshot(qCadetes, (snapshot) => {
      const rawCadetes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const listaAlfabetica = [...rawCadetes].sort((a, b) => limparNomeParaSort(a.nome).localeCompare(limparNomeParaSort(b.nome)));
      
      const listaCadetesComCalculo = rawCadetes.map(c => {
          const index = listaAlfabetica.findIndex(x => x.id === c.id);
          const numCalculado = index + 1;
          const extracted = extrairNumero(c);
          
          return {
              ...c, 
              numero_calculado: numCalculado,
              ordem_antiguidade: typeof extracted === 'number' ? extracted : numCalculado
          };
      });

      listaCadetesComCalculo.sort((a, b) => {
          if (a.ordem_antiguidade !== b.ordem_antiguidade) {
              return a.ordem_antiguidade - b.ordem_antiguidade;
          }
          return limparNomeParaSort(a.nome).localeCompare(limparNomeParaSort(b.nome));
      });
      
      setCadetes(listaCadetesComCalculo);
    }, (err) => console.error("Erro (Cadetes RT):", err));

    const qEscolhas = query(collection(db, paths.escolhas), where("pelotao_id", "==", pelotaoId));
    const unsubEscolhas = onSnapshot(qEscolhas, (snapshot) => {
      const lista = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setEscolhas(lista);
      // OTIMIZAÇÃO: Filtramos da lista menor, baseada no próprio pelotão
      setMeuHistorico(lista.filter(e => e.cadete_id === userProfile.id));
    }, (err) => console.error("Erro (Escolhas RT):", err));

    const qDatas = query(
        collection(db, paths.datasEstagio),
        where("pelotao_id", "in", [pelotaoId, null])
    );
    const unsubDatas = onSnapshot(qDatas, (snapshot) => {
      setDatasEstagio(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    }, (err) => console.error("Erro (Datas RT):", err));

    const unsubTodasEscolhas = onSnapshot(collection(db, paths.escolhas), (snapshot) => {
            const all = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setTodasEscolhas(all);
    }, (err) => console.error("Erro ao buscar todas escolhas:", err));

    // --- DADOS ESTÁTICOS (Buscados 1 única vez, economiza leituras) ---
    const fetchStaticData = async () => {
        try {
            const [locaisSnap, pelotoesSnap] = await Promise.all([
                getDocs(collection(db, paths.locais)),
                getDocs(collection(db, paths.pelotoes))
            ]);
            
            setLocais(locaisSnap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            
            const listaPelotoes = pelotoesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAllPelotoes(listaPelotoes.filter(p => p.id !== pelotaoId).sort((a, b) => a.nome.localeCompare(b.nome)));
        } catch (error) {
            console.error("Erro ao buscar dados estáticos:", error);
        }
    };

    fetchStaticData();
    setLoading(false); 

    return () => {
      unsubPelotaoData();
      unsubCadetes();
      unsubEscolhas();
      unsubDatas();
      unsubTodasEscolhas();
    };

  }, [pelotaoId, userProfile.id, activeTab]);

  // --------------------------------------------------------------------------
  // LÓGICA DA FILA E PROCESSAMENTO DE DADOS
  // --------------------------------------------------------------------------
  
  const isFilaLiberada = pelotaoData?.liberado_escolhas === true;
  const meuEscalanteAoVivo = cadetes.find(c => c.id === userProfile.id);
  const isRepeatAllowed = meuEscalanteAoVivo?.allow_repeat_local === true;

  const handleToggleFila = async () => {
      if (!pelotaoData) return;
      const novoStatus = !isFilaLiberada;
      const acao = novoStatus ? "LIBERAR Rodada de Escolhas" : "PAUSAR Rodada de Escolhas";
      
      if (window.confirm(`Tem certeza que deseja ${novoStatus ? 'LIBERAR' : 'PAUSAR'} a rodada de escolhas para os cadetes?`)) {
          try {
              await updateDoc(doc(db, paths.pelotoes, pelotaoId), {
                  liberado_escolhas: novoStatus
              });
              await logAuditoria(userProfile, acao, { status: novoStatus });
          } catch (err) {
              setGlobalError("Erro ao alterar status da fila: " + err.message);
          }
      }
  };
  
  const handleToggleSkipQueue = async (cadete) => {
      if (!cadete || !cadete.id) return;
      const novoStatus = !cadete.skip_queue; 
      const action = novoStatus ? "Pular Cadete na Fila" : "Retomar Cadete na Fila";
      
      try {
          await updateDoc(doc(db, paths.users, cadete.id), {
              skip_queue: novoStatus
          });
          await logAuditoria(userProfile, action, { cadete: cadete.nome, skip_queue: novoStatus });
      } catch (err) {
          setGlobalError("Erro ao alterar status de pular fila: " + err.message);
      }
  };

  const handleToggleRepeatLocal = async (cadete) => {
    if (!cadete || !cadete.id) return;
    const novoStatus = !cadete.allow_repeat_local; 
    const action = novoStatus ? "Permitir Repetição de Local" : "Bloquear Repetição de Local";
    
    try {
        await updateDoc(doc(db, paths.users, cadete.id), {
            allow_repeat_local: novoStatus
        });
        await logAuditoria(userProfile, action, { cadete: cadete.nome, allow_repeat_local: novoStatus });
    } catch (err) {
        setGlobalError("Erro ao alterar status de repetição de local: " + err.message);
    }
  };
  
  const filaUnificada = useMemo(() => {
    const locaisFormatados = cadetes.map(c => ({
        ...c,
        tipo_vinculo: 'LOCAL', 
        // OTIMIZAÇÃO: Usando a array 'escolhas' (específica do pelotão)
        escolhasCadete: escolhas.filter(e => e.cadete_id === c.id) 
    }));

    const escolhasExternas = escolhas.filter(e => e.tipo === 'EXTERNO');
    const externosFormatados = escolhasExternas.map(e => ({
        id: `ext_${e.id}`, 
        nome: e.cadete_nome,
        ordem_antiguidade: parseInt(e.ordem_externa, 10) || 999, 
        pelotao_origem: e.pelotao_origem || 'N/D',
        tipo_vinculo: 'EXTERNO',
        escolhasCadete: [e], 
        status: 'externo_alocado'
    }));

    const lista = [...locaisFormatados, ...externosFormatados];
    
    return lista.sort((a, b) => {
        const tipoA = a.tipo_vinculo === 'LOCAL' ? 0 : 1;
        const tipoB = b.tipo_vinculo === 'LOCAL' ? 0 : 1;
        
        if (tipoA !== tipoB) return tipoA - tipoB; 
        if (a.ordem_antiguidade !== b.ordem_antiguidade) return a.ordem_antiguidade - b.ordem_antiguidade;
        
        return (a.nome || '').localeCompare(b.nome || '');
    });
  }, [cadetes, escolhas]); // OTIMIZAÇÃO: todasEscolhas removido das dependências

  const diasDisponiveisDoPelotao = useMemo(() => {
    return new Set(datasEstagio.map(d => getNormalizedDateString(d.data)));
  }, [datasEstagio]);

  const cadetesCompletaramRodada = useMemo(() => {
    const completionMap = new Map();
    const totalDias = diasDisponiveisDoPelotao.size;

    cadetes.forEach(cadete => {
        // OTIMIZAÇÃO: Usando 'escolhas'
        const minhas = escolhas.filter(e => e.cadete_id === cadete.id);
        
        if (minhas.some(e => e.opcao_impossibilidade && !e.data)) {
            completionMap.set(cadete.id, true);
            return;
        }

        const diasCobertos = new Set();
        minhas.forEach(e => {
            if (e.data) {
                const dStr = getNormalizedDateString(e.data);
                if (diasDisponiveisDoPelotao.has(dStr)) { 
                    diasCobertos.add(dStr);
                }
            }
        });
        
        completionMap.set(cadete.id, diasCobertos.size >= totalDias && totalDias > 0);
    });
    
    return completionMap;
  }, [escolhas, cadetes, diasDisponiveisDoPelotao]); // OTIMIZAÇÃO: todasEscolhas removido das dependências

  const proximoDaFila = useMemo(() => {
      if (cadetes.length === 0) return null;
      if (datasEstagio.length === 0) return null; 

      for (const usuario of cadetes) {
        if (usuario.skip_queue) continue; 
        if (!cadetesCompletaramRodada.get(usuario.id)) return usuario;
      }
      return null; 
  }, [cadetes, cadetesCompletaramRodada, datasEstagio]);

  const { isRoundComplete, diasDisponiveisGlobal, diasEmQueJaEscolhiOuPulei } = useMemo(() => {
    const temImpossibilidadeGeral = meuHistorico.some(e => e.opcao_impossibilidade && !e.data);

    const diasCobertos = new Set();
    meuHistorico.forEach(e => {
        if (e.data) {
            const dStr = getNormalizedDateString(e.data);
            if (diasDisponiveisDoPelotao.has(dStr)) {
                diasCobertos.add(dStr);
            }
        }
    });

    const totalDias = diasDisponiveisDoPelotao.size;
    const complete = temImpossibilidadeGeral || (totalDias > 0 && diasCobertos.size >= totalDias);

    return { 
        isRoundComplete: complete, 
        diasDisponiveisGlobal: diasDisponiveisDoPelotao,
        diasEmQueJaEscolhiOuPulei: diasCobertos 
    };
  }, [meuHistorico, diasDisponiveisDoPelotao]);

  const pulouGeralCheck = meuHistorico.some(e => e.opcao_impossibilidade && !e.data);
  const isMinhaVezNaFila = proximoDaFila?.id === userProfile.id && !meuEscalanteAoVivo?.skip_queue;
  const jaInteragiu = meuHistorico.length > 0; 
  const isRoundOpen = isMinhaVezNaFila && !pulouGeralCheck; 

  const opcoesDisponiveis = useMemo(() => {
    if (!isRoundOpen || locais.length === 0 || datasEstagio.length === 0) return [];

    const locaisOndeJaEstive = new Set(meuHistorico.map(e => e.local_id));
    
    const ocupacaoGlobal = new Map(); 
    todasEscolhas.forEach(e => {
      if (!e.opcao_impossibilidade && e.data_estagio_id) {
        ocupacaoGlobal.set(e.data_estagio_id, (ocupacaoGlobal.get(e.data_estagio_id) || 0) + 1);
      }
    });

    const meusDiasImpossiveis = new Set(
        meuHistorico.filter(e => e.opcao_impossibilidade && e.data)
        .map(e => getNormalizedDateString(e.data))
    );

    const minhasVagasEscolhidas = new Set(
        meuHistorico.filter(e => !e.opcao_impossibilidade && e.data_estagio_id)
        .map(e => e.data_estagio_id)
    );

    const opcoes = datasEstagio.map(dataEst => {
      const local = locais.find(l => l.id === dataEst.local_id);
      if (!local) return null;

      const dataEstagioId = dataEst.id;
      const dataStr = getNormalizedDateString(dataEst.data);
      let invalido = null;

      if (pulouGeralCheck) invalido = "Impossibilidade Geral.";
      
      if (!isRepeatAllowed && locaisOndeJaEstive.has(local.id)) { 
           if (!minhasVagasEscolhidas.has(dataEstagioId)) {
               invalido = "Você já estagiou neste local.";
           }
      }

      if (meusDiasImpossiveis.has(dataStr)) invalido = "Dia marcado como impossível.";
      if (minhasVagasEscolhidas.has(dataEstagioId)) invalido = "Você já escolheu esta vaga.";
      
      const vagasOcupadas = ocupacaoGlobal.get(dataEstagioId) || 0;
      if (!invalido) {
          if (dataEst.tipo === 'PCO' && vagasOcupadas > 0 && dataEst.capacidade <= 1) invalido = "Vaga preenchida.";
          if (dataEst.tipo === 'PJ' && vagasOcupadas >= dataEst.capacidade) invalido = "Vagas esgotadas.";
      }

      return { ...dataEst, local_nome: local.nome, cpa_nome: getCPAName(local.nome), invalido: invalido };
    }).filter(o => o !== null);

    return opcoes;
  }, [isRoundOpen, locais, datasEstagio, meuHistorico, todasEscolhas, pulouGeralCheck, isRepeatAllowed]);

  const opcoesAgrupadas = useMemo(() => {
    const grupos = { PCO_POR_DIA: new Map(), PJ_POR_DIA: new Map() };
    const sortedOpcoes = [...opcoesDisponiveis].sort((a, b) => {
      if (a.tipo === 'PCO' && b.tipo === 'PJ') return -1;
      if (a.tipo === 'PJ' && b.tipo === 'PCO') return 1;
      const dateA = a.data?.toDate() || 0;
      const dateB = b.data?.toDate() || 0;
      return dateA - dateB;
    });

    sortedOpcoes.forEach(opcao => {
      const tipoKey = opcao.tipo; 
      const dataStr = getNormalizedDateString(opcao.data);
      const cpaKey = opcao.cpa_nome;

      if (tipoKey === 'PCO') {
          if (!grupos.PCO_POR_DIA.has(dataStr)) {
              grupos.PCO_POR_DIA.set(dataStr, { dataTimestamp: opcao.data, dataNome: formatarData(opcao.data), opcoesPorCPA: new Map() });
          }
          const diaGroup = grupos.PCO_POR_DIA.get(dataStr);
          if (!diaGroup.opcoesPorCPA.has(cpaKey)) diaGroup.opcoesPorCPA.set(cpaKey, []);
          diaGroup.opcoesPorCPA.get(cpaKey).push(opcao);
      } else if (tipoKey === 'PJ') {
          if (!grupos.PJ_POR_DIA.has(dataStr)) {
            grupos.PJ_POR_DIA.set(dataStr, { dataTimestamp: opcao.data, dataNome: formatarData(opcao.data), opcoes: [] });
          }
          grupos.PJ_POR_DIA.get(dataStr).opcoes.push(opcao);
      }
    });
    return grupos;
  }, [opcoesDisponiveis]);
  
  const handleAddExternalCadete = (data) => { setCadetesExternos(prev => [...prev, data]); };
  
  const handleResetPasswordCadete = async (cadeteId, cpf, nome) => {
    const cpfLimpo = limparCPF(cpf);
    if (cpfLimpo.length !== 11) { setError("Erro: CPF inválido para reset de senha."); return; }
    if (!window.confirm(`Tem certeza que deseja resetar a senha de ${nome}? A nova senha será o CPF: ${cpfLimpo}.`)) return;
    setProcessing(true); setError(''); 
    try {
        const docRef = doc(db, paths.users, cadeteId);
        await updateDoc(docRef, { senha: cpfLimpo }); 
        await logAuditoria(userProfile, "Reset de Senha de Cadete", { cadeteId, cadeteNome: nome });
        alert(`Sucesso! A senha de ${nome} foi resetada para o CPF.`);
    } catch (err) {
        console.error("Erro ao resetar senha:", err);
        setError("Falha ao resetar a senha: " + err.message);
    } finally { setProcessing(false); }
  };

  const handleConfirmarAlocacaoExterna = async (dados) => {
    setProcessing(true);
    setGlobalError('');
    try {
        const qVagas = query(collection(db, paths.escolhas),
            where("data_estagio_id", "==", dados.data_estagio_id),
            where("opcao_impossibilidade", "==", false)
        );
        const snap = await getDocs(qVagas);
        const vagasOcupadas = snap.size;
        const capacidade = dados.stageData.tipo === 'PCO' ? 1 : dados.stageData.capacidade;

        if (vagasOcupadas >= capacidade) {
            throw new Error("Não foi possível alocar: Vagas esgotadas para este estágio.");
        }

        await runTransaction(db, async (transaction) => {
            const novaRef = doc(collection(db, paths.escolhas));
            transaction.set(novaRef, {
                cadete_id: dados.id || `EXT-${Date.now()}`, 
                cadete_nome: `${dados.nome} (${dados.pelotao_origem})`, 
                matricula: dados.re || 'N/D', 
                pelotao_id: userProfile.pelotao_id, 
                pelotao_origem: dados.pelotao_origem, 
                ordem_externa: parseInt(dados.ordem_antiguidade, 10), 
                
                data_estagio_id: dados.data_estagio_id,
                local_id: dados.stageData.local_id,
                local_nome: dados.stageData.local_nome,
                data: dados.stageData.data,
                tipo: 'EXTERNO', 
                turno: dados.stageData.turno,
                opcao_impossibilidade: false,
                criado_em: serverTimestamp()
            });

            const logRef = doc(collection(db, paths.logs));
            transaction.set(logRef, {
                usuario_id: userProfile.id,
                usuario_nome: userProfile.nome,
                acao: "Alocação Cadete Externo",
                payload: JSON.stringify({ nome: dados.nome, pelotao: dados.pelotao_origem, local: dados.stageData.local_nome }),
                criado_em: serverTimestamp()
            });
        });

        setShowAlocarExternoModal(false);
    } catch (err) {
        console.error(err);
        setGlobalError(err.message);
    } finally {
        setProcessing(false);
    }
  };

  const handleOpenModal = (view, item = null) => {
    if (view === 'dataEstagio' && isFilaLiberada && !item) {
        alert("A fila de escolhas está LIGADA. Você deve PAUSAR a rodada antes de criar novas vagas de estágio.");
        return;
    }

    setModalView(view);
    setCurrentItem(item);
    setError(''); 
    setProcessing(false); 

    if (view === 'deleteBulk') {
        setDeleteBulkDate('');
    } else if (view === 'assinaturas') {
        setFormData(prev => ({
            ...prev,
            nome_escalante: pelotaoData?.nome_escalante || getNomeCompleto(userProfile.nome),
            posto_escalante: pelotaoData?.posto_escalante || 'Cad PM',
            nome_cmt_pelotao: pelotaoData?.nome_cmt_pelotao || '',
            posto_cmt_pelotao: pelotaoData?.posto_cmt_pelotao || '1º Ten PM',
        }));
    } else if (item) {
      if (view === 'dataEstagio') {
        const dataFormatada = item.data.toDate().toISOString().split('T')[0];
        setFormData({ ...item, data: dataFormatada });
      } else {
        setFormData(item);
      }
    } else {
      const defaults = {
        dataEstagio: { 
            data: '', 
            tipo: 'PCO', 
            pcoDia: true,     
            pcoNoite: false,  
            creationMode: 'auto',
            pjNextDayExpediente: 'nao',
            pjFridayExpediente: 'sim',
            manualVagasDia: 0,
            manualVagasNoite: 0
        },
      };
      setFormData(defaults[view]);
    }
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    let val = type === 'checkbox' ? checked : (type === 'number' ? Number(value) : value);
    if (type === 'number') val = parseInt(val, 10) || 0;
    if (name === 'telefone') val = value.replace(/\D/g, "");
    setFormData(prev => ({ ...prev, [name]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError(''); 

    try {
      let dataToSave = { ...formData };
      let acao = '';

      if (modalView === 'assinaturas') {
        if (!dataToSave.nome_escalante || !dataToSave.posto_escalante || !dataToSave.nome_cmt_pelotao || !dataToSave.posto_cmt_pelotao) {
             throw new Error("Todos os campos de assinatura são obrigatórios.");
        }
        acao = "Atualização de Assinaturas";
        const docRef = doc(db, paths.pelotoes, pelotaoId);
        await updateDoc(docRef, {
            nome_escalante: dataToSave.nome_escalante,
            posto_escalante: dataToSave.posto_escalante,
            nome_cmt_pelotao: dataToSave.nome_cmt_pelotao,
            posto_cmt_pelotao: dataToSave.posto_cmt_pelotao,
        });
        
      } else if (modalView === 'dataEstagio') {
        acao = "Criação de Datas de Estágio (em Lote)";
        const dataSelecionada = new Date(dataToSave.data + 'T12:00:00Z');
        const diaSelecionado = dataSelecionada.getDay();
        const pelotaoIdParaSalvar = userProfile.pelotao_id;
        const batch = writeBatch(db);

        if (dataToSave.tipo === 'PCO') {
          if (!dataToSave.pcoDia && !dataToSave.pcoNoite) {
              throw new Error("Selecione pelo menos um turno (Dia ou Noite) para criar vagas PCO.");
          }
        
          const pcoLocals = locais.filter(l => l.tipo === 'PCO');
          if (pcoLocals.length === 0) throw new Error("Nenhum local PCO cadastrado pelo Admin.");

          if (dataToSave.pcoDia) {
              pcoLocals.forEach(local => {
                const newDocRef = doc(collection(db, paths.datasEstagio));
                batch.set(newDocRef, {
                  id: newDocRef.id,
                  local_id: local.id,
                  local_nome: local.nome,
                  data: Timestamp.fromDate(dataSelecionada),
                  tipo: 'PCO',
                  turno: 'dia', 
                  capacidade: 1, 
                  pelotao_id: pelotaoIdParaSalvar
                });
              });
          }
          
          if (dataToSave.pcoNoite) {
              pcoLocals.forEach(local => {
                const newDocRef = doc(collection(db, paths.datasEstagio));
                batch.set(newDocRef, {
                  id: newDocRef.id,
                  local_id: local.id,
                  local_nome: local.nome,
                  data: Timestamp.fromDate(dataSelecionada),
                  tipo: 'PCO',
                  turno: 'noite', 
                  capacidade: 1, 
                  pelotao_id: pelotaoIdParaSalvar
                });
              });
          }

        } else if (dataToSave.tipo === 'PJ') {
          const pjLocals = locais.filter(l => l.tipo === 'PJ');
          if (pjLocals.length === 0) throw new Error("Nenhum local PJ cadastrado pelo Admin.");

          const createVagas = (turno, capacidade) => {
            if (capacidade <= 0) return; 
            pjLocals.forEach(local => {
              const newDocRef = doc(collection(db, paths.datasEstagio));
              batch.set(newDocRef, {
                id: newDocRef.id,
                local_id: local.id,
                local_nome: local.nome,
                data: Timestamp.fromDate(dataSelecionada),
                tipo: 'PJ',
                turno: turno,
                capacidade: capacidade,
                pelotao_id: pelotaoIdParaSalvar
              });
            });
          };

          if (dataToSave.creationMode === 'manual') {
              const vagasDia = parseInt(dataToSave.manualVagasDia, 10) || 0;
              const vagasNoite = parseInt(dataToSave.manualVagasNoite, 10) || 0;

              if (vagasDia === 0 && vagasNoite === 0) {
                  throw new Error("Modo Manual: Pelo menos uma vaga (dia ou noite) deve ser maior que 0.");
              }

              createVagas('dia', vagasDia);
              createVagas('noite', vagasNoite);

          } else {
              if (diaSelecionado >= 0 && diaSelecionado <= 4) {
                  if (dataToSave.pjNextDayExpediente === 'sim') {
                      createVagas('dia', 2);
                      createVagas('noite', 1);
                  } else {
                      createVagas('dia', 3);
                  }
              } else if (diaSelecionado === 5) {
                  if (dataToSave.pjFridayExpediente === 'sim') {
                      createVagas('noite', 3);
                  } else {
                      createVagas('dia', 2);
                      createVagas('noite', 1);
                  }
              } else if (diaSelecionado === 6) {
                  createVagas('dia', 2);
                  createVagas('noite', 1);
              } else {
                  throw new Error("Dia da semana inválido para PJ.");
              }
          }
        }

        await batch.commit(); 

      } else if (modalView === 'cadete') {
        acao = "Edição de Perfil de Cadete";
        const docRef = doc(db, paths.users, dataToSave.id);
        await updateDoc(docRef, {
            nome: dataToSave.nome,
            cpf: dataToSave.cpf,
            matricula: dataToSave.matricula,
            ordem_antiguidade: parseInt(dataToSave.ordem_antiguidade, 10),
            telefone: dataToSave.telefone || ''
        });
      } else {
        throw new Error("Ação de salvamento desconhecida.");
      }

      await logAuditoria(userProfile, acao, dataToSave);
      setShowModal(false);

    } catch (err) {
      console.error("Erro no CRUD:", err);
      setError(err.message);
    } finally {
      setProcessing(false);
    }
  };
  
  const handleOpenGerenciarEscolha = (escolha) => {
      setEscolhaEmEdicao(escolha);
      setNovaVagaId(''); 
      setError('');
      setShowGerenciarEscolhaModal(true);
  };

  const handleTrocarEstagio = async () => {
      if (!escolhaEmEdicao || !novaVagaId) return;
      setProcessing(true);
      setError('');

      try {
          const vagaAlvo = datasEstagio.find(d => d.id === novaVagaId);
          if (!vagaAlvo) throw new Error("Vaga selecionada não encontrada.");

          await runTransaction(db, async (transaction) => {
              const qVagas = query(collection(db, paths.escolhas),
                  where("data_estagio_id", "==", novaVagaId),
                  where("opcao_impossibilidade", "==", false)
              );
              const snap = await getDocs(qVagas);
              const ocupadas = snap.size;
              const capacidade = vagaAlvo.tipo === 'PCO' ? 1 : vagaAlvo.capacidade;
              
              if (ocupadas >= capacidade) {
                  throw new Error("A vaga selecionada foi preenchida neste instante.");
              }

              const escolhaRef = doc(db, paths.escolhas, escolhaEmEdicao.id);
              const isExterno = escolhaEmEdicao.tipo === 'EXTERNO';
              const novoTipo = isExterno ? 'EXTERNO' : vagaAlvo.tipo;

              transaction.update(escolhaRef, {
                  data_estagio_id: vagaAlvo.id,
                  local_id: vagaAlvo.local_id,
                  local_nome: vagaAlvo.local_nome,
                  data: vagaAlvo.data,
                  tipo: novoTipo, 
                  turno: vagaAlvo.turno,
                  opcao_impossibilidade: false, 
                  motivo_impossibilidade: null,
                  editado_por_escalante_em: serverTimestamp()
              });

              const logRef = doc(collection(db, paths.logs));
              transaction.set(logRef, {
                  usuario_id: userProfile.id,
                  usuario_nome: userProfile.nome,
                  acao: "Troca de Estágio (Escalante)",
                  payload: JSON.stringify({ 
                      cadete: escolhaEmEdicao.cadete_nome,
                      de: escolhaEmEdicao.local_nome, 
                      para: vagaAlvo.local_nome 
                  }),
                  criado_em: serverTimestamp()
              });
          });

          setShowGerenciarEscolhaModal(false);
          setEscolhaEmEdicao(null);
      } catch (err) {
          console.error("Erro na troca:", err);
          setError(err.message);
      } finally {
          setProcessing(false);
      }
  };

  const handleConfirmarCancelamento = async (escolha, motivo) => {
      if (!escolha || !motivo) throw new Error("Motivo do cancelamento é obrigatório.");
      const cadete = cadetes.find(c => c.id === escolha.cadete_id);
      const isExterno = escolha.tipo === 'EXTERNO';
      
      try {
          await runTransaction(db, async (transaction) => {
              const escolhaRef = doc(db, paths.escolhas, escolha.id);
              transaction.delete(escolhaRef);

              if (!isExterno && cadete) {
                  const novaImpossibilidadeRef = doc(collection(db, paths.escolhas));
                  transaction.set(novaImpossibilidadeRef, {
                      cadete_id: cadete.id,
                      cadete_nome: cadete.nome,
                      pelotao_id: cadete.pelotao_id,
                      data_estagio_id: null,
                      local_id: null,
                      local_nome: "IMPOSSIBILIDADE",
                      data: escolha.data, 
                      tipo: null,
                      turno: null,
                      opcao_impossibilidade: true,
                      motivo_impossibilidade: `CANCELADO PELO ESCALANTE: ${motivo}`,
                      criado_em: serverTimestamp(),
                  });
              }
              
              const logRef = doc(collection(db, paths.logs));
              transaction.set(logRef, {
                  usuario_id: userProfile.id,
                  usuario_nome: userProfile.nome,
                  acao: "Cancelamento de Escolha por Escalante",
                  payload: JSON.stringify({
                      cadete: escolha.cadete_nome,
                      local: escolha.local_nome,
                      data: formatarData(escolha.data),
                      motivo_escalante: motivo,
                  }),
                  criado_em: serverTimestamp(),
              });
          });
      } catch (error) {
          console.error("Erro ao cancelar escolha:", error);
          throw new Error("Falha ao cancelar a escolha na transação.");
      }
  };

  const confirmDelete = (pathKey, id, deleteBulkDate = null) => {
    let finalPathKey = pathKey === 'cadete' ? 'users' : pathKey;
    if (pathKey === 'dataEstagio') {
      finalPathKey = 'datasEstagio';
    }
    if (pathKey === 'escolha') {
      finalPathKey = 'escolha';
    }
    setDeleteAction({ pathKey: finalPathKey, id, deleteBulkDate });
    setShowConfirm(true);
  };

  const handleDelete = async () => {
    if (!deleteAction) return;
    const { pathKey, id, deleteBulkDate } = deleteAction;

    try {
      if (pathKey === 'escolha') {
          await deleteDoc(doc(db, paths.escolhas, id));
          await logAuditoria(userProfile, "Exclusão de Escolha (Escalante)", { id });
          setShowGerenciarEscolhaModal(false); 
      }
      
      if (pathKey === 'datasEstagio') {
        if (deleteBulkDate) {
            const dataNormalizada = deleteBulkDate;
            const qDatas = query(collection(db, paths.datasEstagio));
            const datasSnapshot = await getDocs(qDatas);
            const batch = writeBatch(db);
            let count = 0;
            let countEscolhas = 0;

            datasSnapshot.docs.forEach(doc => {
                const data = doc.data().data;
                const dataStr = getNormalizedDateString(data);
                const vagaPelotaoId = doc.data().pelotao_id;
                if (dataStr === dataNormalizada && vagaPelotaoId === userProfile.pelotao_id) {
                    const escolhasAssociadas = todasEscolhas.filter(e => e.data_estagio_id === doc.id && !e.opcao_impossibilidade);
                    if (escolhasAssociadas.length > 0) {
                        countEscolhas += escolhasAssociadas.length;
                        throw new Error(`Existem ${countEscolhas} escolhas de cadetes associadas a uma das datas (ID: ${doc.id}). Exclusão em bloco cancelada.`);
                    }
                    batch.delete(doc.ref);
                    count++;
                }
            });

            if (countEscolhas > 0) return; 

            await batch.commit();
            await logAuditoria(userProfile, "Exclusão em Bloco de Datas de Estágio", { data: dataNormalizada, count });
            setError(`Excluídas ${count} datas de estágio do dia ${formatarData(new Date(deleteBulkDate))}.`);
        } else {
            const q = query(collection(db, paths.escolhas), where("data_estagio_id", "==", id));
            const snap = await getDocs(q);
            if (!snap.empty) {
              throw new Error("Não é possível excluir. Esta data possui escolhas de cadetes associadas.");
            }
            await deleteDoc(doc(db, paths[pathKey], id));
            await logAuditoria(userProfile, "Exclusão de Data de Estágio", { id });
        }
      }
      if (pathKey === 'users') {
        if (id === userProfile.id) {
          throw new Error("Não é possível excluir o seu próprio usuário.");
        }
        await deleteDoc(doc(db, paths[pathKey], id));
        await logAuditoria(userProfile, "Exclusão de Cadete", { id });
      }

      setShowConfirm(false);
      setDeleteAction(null);
      if (!deleteBulkDate) setError(''); 
      setShowModal(false); 
      if (pathKey !== 'escolha') {
          setActiveTab('datas'); 
      }

    } catch (err) {
      console.error("Erro ao excluir:", err);
      setError(err.message);
      setShowConfirm(false);
      setShowModal(false);
    }
  };

  const handleSelecionarOpcao = (opcao) => {
    setSelectedEstagio(opcao);
    setShowModalEscolha(true);
  };

  const handleAbrirImpossibilidadePorDia = (dateString, dateTimestamp) => {
      setSelectedDateToSkip({ dateString, dateTimestamp });
      setMotivoImpossibilidade('');
      setShowModalImpossibilidade(true);
  };
  
  const handleConfirmarEscolha = async () => {
    if (!selectedEstagio || processing) return;
    setProcessing(true);
    setError('');

    try {
        if (!db || !paths.datasEstagio || !paths.escolhas || !paths.logs) {
            throw new Error("Erro de Configuração do Sistema. Contate o Admin.");
        }

        await runTransaction(db, async (transaction) => {
            const qVagas = query(collection(db, paths.escolhas),
                where("data_estagio_id", "==", selectedEstagio.id),
                where("opcao_impossibilidade", "==", false)
            );
            const vagasSnapshot = await getDocs(qVagas); 

            const dataEstagioRef = doc(db, paths.datasEstagio, selectedEstagio.id);
            const dataEstagioDoc = await transaction.get(dataEstagioRef);
            if (!dataEstagioDoc.exists()) throw new Error("Data de estágio não existe mais.");
            const dataEstagio = dataEstagioDoc.data();
            
            const vagasOcupadas = vagasSnapshot.docs.length;
            
            if (dataEstagio.tipo === 'PCO' && vagasOcupadas >= 1) { 
              throw new Error("Vaga PCO foi preenchida enquanto você decidia.");
            }
            if (dataEstagio.tipo === 'PJ' && vagasOcupadas >= dataEstagio.capacidade) { 
              throw new Error("Vagas PJ esgotaram enquanto você decidia.");
            }

            const dataNormalizadaStr = getNormalizedDateString(dataEstagio.data);
            const qMinhasEscolhas = query(collection(db, paths.escolhas), where("cadete_id", "==", userProfile.id));
            const minhasEscolhasSnapshot = await getDocs(qMinhasEscolhas);

            if (!minhasEscolhasSnapshot.empty) {
                for (const doc of minhasEscolhasSnapshot.docs) {
                    const hist = doc.data();
                    
                    if (!isRepeatAllowed && hist.local_id === dataEstagio.local_id) throw new Error("Você não pode escolher o mesmo local duas vezes.");

                    if (hist.data) {
                        const histDataNormalizadaStr = getNormalizedDateString(hist.data);
                        if (histDataNormalizadaStr === dataNormalizadaStr) throw new Error("Você já tem uma escolha ou impossibilidade marcada para o dia " + formatarData(dataEstagio.data));
                    }
                    if (hist.opcao_impossibilidade && !hist.data) throw new Error("Impossibilidade Geral marcada para esta rodada. Não é possível escolher.");
                }
            }
            
            const novaEscolhaRef = doc(collection(db, paths.escolhas));
            transaction.set(novaEscolhaRef, {
                cadete_id: userProfile.id,
                cadete_nome: userProfile.nome,
                pelotao_id: userProfile.pelotao_id,
                data_estagio_id: selectedEstagio.id,
                local_id: dataEstagio.local_id,
                local_nome: selectedEstagio.local_nome,
                data: dataEstagio.data,
                tipo: dataEstagio.tipo,
                turno: dataEstagio.turno,
                opcao_impossibilidade: false,
                motivo_impossibilidade: null,
                criado_em: serverTimestamp(),
            });
            
            const cleanedEstagio = {
                id: selectedEstagio.id,
                local_id: selectedEstagio.local_id,
                local_nome: selectedEstagio.local_nome,
                tipo: selectedEstagio.tipo,
                turno: selectedEstagio.turno,
                data: formatarData(selectedEstagio.data), 
            };
            
            const logRef = doc(collection(db, paths.logs));
            const logPayload = {
                usuario_id: userProfile.id,
                usuario_nome: userProfile.nome,
                acao: "Confirmação de Escolha (Escalante)",
                payload: JSON.stringify(cleanedEstagio),
            };
            transaction.set(logRef, { ...logPayload, criado_em: serverTimestamp() });
        });


        setShowModalEscolha(false);
        setSelectedEstagio(null);
    } catch (error) {
        console.error("Erro na Transação de Escolha (Escalante):", error);
        const errorMessage = error.message ? error.message : "Erro desconhecido na transação.";
        setError(errorMessage);
    } finally {
        setProcessing(false);
    }
  };

  const handleConfirmarImpossibilidade = async () => {
    if (processing) return;
    setProcessing(true);
    setError('');

    try {
      if (selectedDateToSkip) {
        const { dateString, dateTimestamp } = selectedDateToSkip;
        const qMinhasDatas = query(collection(db, paths.escolhas), where("cadete_id", "==", userProfile.id));
        const minhasDatasSnapshot = await getDocs(qMinhasDatas);
        const dataNormalizadaStr = dateString;

        if (!minhasDatasSnapshot.empty) {
            for (const doc of minhasDatasSnapshot.docs) {
                const hist = doc.data();
                if (hist.opcao_impossibilidade && !hist.data) throw new Error("Impossibilidade Geral já marcada. Não é possível marcar por dia.");
                if (hist.data) {
                    const histDataNormalizadaStr = getNormalizedDateString(hist.data);
                    if (histDataNormalizadaStr === dataNormalizadaStr) throw new Error("Você já tem uma escolha ou impossibilidade marcada para o dia " + formatarData(dateTimestamp));
                }
            }
        }
        
        await addDoc(collection(db, paths.escolhas), {
          cadete_id: userProfile.id,
          cadete_nome: userProfile.nome,
          pelotao_id: userProfile.pelotao_id,
          data_estagio_id: null,
          local_id: null,
          local_nome: "IMPOSSIBILIDADE",
          data: dateTimestamp,
          tipo: null,
          turno: null,
          opcao_impossibilidade: true,
          motivo_impossibilidade: motivoImpossibilidade || "Não especificado.",
          criado_em: serverTimestamp(),
        });
        await logAuditoria(userProfile, "Marcou Impossibilidade Por Dia (Escalante)", { data: dateString, motivo: motivoImpossibilidade });
      } else { throw new Error("Opção de Impossibilidade Geral removida. Use o botão no dia específico."); }
      setShowModalImpossibilidade(false); setSelectedDateToSkip(null); setSelectedEstagio(null);
    } catch (error) {
      console.error("Erro ao marcar impossibilidade:", error);
      setError(error.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setProcessing(false);
    }
  };

  const handleOpenPrintDateModal = () => { setShowPrintDateModal(true); };

  const formatarDataMilitar = (date) => {
    const d = new Date(date);
    const dia = String(d.getDate()).padStart(2, '0');
    const ano = String(d.getFullYear()).slice(-2);
    const meses = ["JAN", "FEV", "MAR", "ABR", "MAI", "JUN", "JUL", "AGO", "SET", "OUT", "NOV", "DEZ"];
    const mes = meses[d.getMonth()];
    return `${dia}${mes}${ano}`;
};
  
  const handlePrintEscala = (dateStr) => {
    const allEscalas = [...escolhas, ...cadetesExternos.map(c => ({ 
        id: `ext-${c.RE}-${c.Nome}-${Date.now()}`, 
        RE: c.RE, 
        Nome: c.Nome, 
        Local: c.Local, 
        Turno: c.Turno, 
        Pelotao: c.Pelotao, 
        data: Timestamp.fromDate(new Date(dateStr + 'T12:00:00Z')), 
        tipo: 'EXTERNO', 
        opcao_impossibilidade: false, 
        Ordem: 999 
    }))];

    const escolhasDia = allEscalas.filter(e => {
        if (e.data && getNormalizedDateString(e.data) === dateStr) return true;
        if (e.tipo === 'EXTERNO' && getNormalizedDateString(e.data) === dateStr) return true;
        return false;
    });

    const escolhasReais = escolhasDia.filter(e => !e.opcao_impossibilidade && e.tipo !== 'EXTERNO');
    const pulos = escolhasDia.filter(e => e.opcao_impossibilidade);
    const cadetesDeFora = escolhasDia.filter(e => e.tipo === 'EXTERNO');
    
    const cadetesMap = new Map(cadetes.map(c => [c.id, c]));

    const groupedByType = { PCO: [], PJ: [], PULO: [], EXTERNO: [] };

    [...escolhasReais, ...pulos, ...cadetesDeFora].forEach(e => {
        let type = e.tipo ? String(e.tipo).toUpperCase().trim() : 'PULO';
        
        if (!groupedByType[type]) {
            console.warn(`Atenção: Tipo desconhecido encontrado na data selecionada: "${e.tipo}"`);
            type = 'PULO'; 
        }

        const cadeteInfo = e.tipo === 'EXTERNO' ? e : cadetesMap.get(e.cadete_id);
        
        let rawPelotao = e.pelotao_origem || e.Pelotao || userProfile.pelotao_nome || 'N/D';
        let pelotaoDisplay = rawPelotao.replace(/^Asp\d+\s*-\s*/, '');

        let turnoFormatado = (e.turno || e.Turno || '-').toUpperCase();
        if(turnoFormatado === 'DIA') turnoFormatado = 'DIURNO';
        if(turnoFormatado === 'NOITE') turnoFormatado = 'NOTURNO';

        let rawNome = cadeteInfo?.nome || e.Nome || e.cadete_nome || 'N/D';
        let cleanNome = rawNome.split('(')[0].trim();

        let reDisplay = cadeteInfo?.matricula || e.RE || e.matricula || 'N/D';

        let item = {
            RE: reDisplay,
            Nome: cleanNome, 
            Pelotao: pelotaoDisplay, 
            Local: e.local_nome || e.Local || '-',
            Tipo: type,
            Turno: turnoFormatado,
            Motivo: e.motivo_impossibilidade || '-',
            Data: e.data,
            Ordem: cadeteInfo?.ordem_antiguidade || e.ordem_externa || 999,
        };
        groupedByType[type].push(item);
    });
    
    ['PCO', 'PJ', 'PULO', 'EXTERNO'].forEach(type => {
        groupedByType[type].sort((a, b) => a.Ordem - b.Ordem);
    });

    let textoObservacoes = '';
    if (groupedByType.PULO.length > 0) {
        const listaNomes = groupedByType.PULO.map(p => `Cad PM ${p.RE} ${p.Nome} (${p.Motivo})`).join('; ');
        textoObservacoes = `IMPOSSIBILIDADES: ${listaNomes}.`;
    }

    const escalanteNome = pelotaoData?.nome_escalante || getNomeCompleto(userProfile.nome) || 'NOME ESCALANTE N/D';
    const postoEscalante = pelotaoData?.posto_escalante || 'Cad PM';
    const cmtPelotaoNome = pelotaoData?.nome_cmt_pelotao || 'NOME CMT PELOTÃO N/D';
    const postoCmtPelotao = pelotaoData?.posto_cmt_pelotao || '1º Ten PM';
    
    const dataMilitar = formatarDataMilitar(dateStr + 'T12:00:00Z');
    let turmaNome = pelotaoData?.turma_nome || "4° CFO"; 
    let pelotaoNomePDF = (pelotaoData?.nome || userProfile.pelotao_nome || "PELOTÃO").replace(/^Asp\d+\s*-\s*/, '');
    const companhiaNome = pelotaoData?.companhia_nome || "COMPANHIA ESCOLA";

    let htmlContent = '';

    const gerarTabelaPDF = (items, tituloTipo, observacoes = '') => {
        if (items.length === 0) return '';
        
        let tituloEscala = "ESCALA DE ATIVIDADE DE TREINAMENTO DE CAMPO DE PARTICIPAÇÃO EM COMANDO OPERACIONAL";
        if (tituloTipo === 'PJ') tituloEscala = "ESCALA DE ATIVIDADE DE TREINAMENTO DE CAMPO DE PRÁTICA JURÍDICA";

        let html = `
            <div style="page-break-after: always; padding: 20px; font-family: 'Arial', sans-serif;">
                <header style="text-align: center; margin-bottom: 20px;">
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">SECRETARIA DA SEGURANÇA PÚBLICA</p>
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">POLÍCIA MILITAR DO ESTADO DE SÃO PAULO</p>
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">ACADEMIA DE POLÍCIA MILITAR DO BARRO BRANCO</p>
                    <br>
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">${tituloEscala}</p>
                    <br>
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">${turmaNome} "${pelotaoNomePDF}" - ${companhiaNome}</p>
                    <br>
                    <p style="margin: 0; font-weight: bold; font-size: 11pt;">DATA: ${dataMilitar}</p>
                </header>

                <table style="width: 100%; border-collapse: collapse; font-size: 10pt;">
                    <thead>
                        <tr>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">PELOTÃO</th>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">RE</th>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">NOME</th>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">UNIDADE</th>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">PERÍODO</th>
                            <th style="border: 1px solid black; padding: 4px; text-align: center;">RESULTADO</th>
                        </tr>
                    </thead>
                    <tbody>
        `;

        items.forEach(item => {
            const resultadoTexto = `Cumprirá em ${dataMilitar}`;
            html += `
                <tr>
                    <td style="border: 1px solid black; padding: 4px; text-align: center;">${item.Pelotao}</td>
                    <td style="border: 1px solid black; padding: 4px; text-align: center;">${item.RE}</td>
                    <td style="border: 1px solid black; padding: 4px;">${item.Nome}</td>
                    <td style="border: 1px solid black; padding: 4px; text-align: center;">${item.Local}</td>
                    <td style="border: 1px solid black; padding: 4px; text-align: center;">${item.Turno}</td>
                    <td style="border: 1px solid black; padding: 4px; text-align: center;">${resultadoTexto}</td>
                </tr>
            `;
        });

        html += `
                    </tbody>
                </table>
        `;

        if (observacoes) {
            html += `
                <div style="margin-top: 20px; font-size: 9pt; width: 100%; text-align: justify;">
                    <span style="font-weight: bold;">OBSERVAÇÕES:</span> ${observacoes}
                </div>
            `;
        }

        html += `
                <div style="margin-top: 50px; display: flex; justify-content: space-between; text-align: center; font-size: 10pt;">
                    <div style="width: 45%;">
                        <p style="margin: 0; font-weight: bold; border-top: 1px solid black; padding-top: 5px;">${escalanteNome}</p>
                        <p style="margin: 0; font-size: 9pt;">${postoEscalante} Escalante Operações ${pelotaoNomePDF}</p>
                    </div>
                    <div style="width: 45%;">
                        <p style="margin: 0; font-weight: bold; border-top: 1px solid black; padding-top: 5px;">${cmtPelotaoNome}</p>
                        <p style="margin: 0; font-size: 9pt;">${postoCmtPelotao} </p>
                    </div>
                </div>
            </div>
        `;
        return html;
    };

    if (groupedByType.PCO.length > 0) htmlContent += gerarTabelaPDF(groupedByType.PCO, 'PCO', textoObservacoes);
    if (groupedByType.PJ.length > 0) htmlContent += gerarTabelaPDF(groupedByType.PJ, 'PJ', textoObservacoes);
    if (groupedByType.EXTERNO.length > 0) htmlContent += gerarTabelaPDF(groupedByType.EXTERNO, 'PCO', textoObservacoes); 

    if (!htmlContent) {
        alert("Não há dados para gerar a escala nesta data.");
        return;
    }

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
        <html>
        <head>
            <title>ESCALA ${dataMilitar}</title>
            <style>
                @page { size: A4; margin: 1cm; }
                body { margin: 0; padding: 0; font-family: 'Arial', sans-serif; }
            </style>
        </head>
        <body>
            ${htmlContent}
        </body>
        </html>
    `);
    printWindow.document.close();
    setTimeout(() => { printWindow.print(); }, 500);
  };
  const datasParaRelatorio = useMemo(() => {
    const datesSet = new Set(datasEstagio.map(d => getNormalizedDateString(d.data)));
    return Array.from(datesSet).sort();
}, [datasEstagio]);
  
  const StatusEscolha = () => {
    if (!userProfile.ordem_antiguidade) {
        return (
            <div className="p-4 bg-gray-100 border border-gray-400 text-gray-800 rounded-lg shadow">
                <h3 className="font-bold text-lg">Configuração Necessária</h3>
                <p>Seu usuário não possui "Ordem de Antiguidade" definida. Vá na aba "Cadetes" e edite seu perfil para habilitar o status.</p>
            </div>
        );
    }

    const pulouGeral = meuHistorico.some(e => e.opcao_impossibilidade && !e.data);
    if (pulouGeral) {
        return (
            <div className="p-4 bg-red-100 border border-red-400 text-red-800 rounded-lg shadow">
                <h3 className="font-bold text-lg">Rodada Pulada (Impossibilidade Geral)</h3>
                <p>Você marcou **Impossibilidade Geral** para esta rodada e não pode mais fazer escolhas.</p>
                <p className="text-sm italic">Motivo: {meuHistorico.find(e => e.opcao_impossibilidade && !e.data)?.motivo_impossibilidade || 'Não especificado.'}</p>
            </div>
        );
    }
    if (isRoundComplete) {
        return (
            <div className="p-4 bg-green-100 border border-green-400 text-green-800 rounded-lg shadow">
                <h3 className="font-bold text-lg">Rodada de Escolhas Concluída!</h3>
                <p>Você já escolheu ou marcou impossibilidade para todos os {diasDisponiveisGlobal.size} dias de estágio disponíveis.</p>
            </div>
        );
    }
    if (proximoDaFila) {
        if (isMinhaVezNaFila) {
            const diasConcluidos = diasEmQueJaEscolhiOuPulei.size;
            const diasFaltantes = diasDisponiveisGlobal.size - diasConcluidos;
            if (!isRoundComplete) {
                return (
                    <div className={`p-4 bg-red-100 border border-red-400 text-red-800 rounded-lg shadow ${diasConcluidos === 0 ? 'animate-pulse' : ''}`}>
                        <h3 className="font-bold text-lg">É A SUA VEZ! (Fila Sequencial)</h3>
                        <p>Faltam **{diasFaltantes}** de **{diasDisponiveisGlobal.size}** dias para concluir sua rodada.</p>
                        <p className="text-sm mt-1">Escolha seu próximo estágio ou marque impossibilidade em um dia.</p>
                    </div>
                );
            }
        } else {
            const minhaOrdem = meuEscalanteAoVivo?.ordem_antiguidade || userProfile.ordem_antiguidade || 999;
            const proximoOrdem = proximoDaFila.ordem_antiguidade;
            const cadetesApenas = cadetes.filter(c => c.nivel === 'cadete' || c.nivel === 'escalante' || c.nivel === 'cadete_academico');
            let pos = 0;
            for (const c of cadetesApenas) { if (c.ordem_antiguidade > proximoOrdem && c.ordem_antiguidade < minhaOrdem) pos++; }
            pos = pos + 1; 
            return (
                <div className="p-4 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded-lg shadow">
                    <h3 className="font-bold text-lg">Aguarde sua vez (Fila Sequencial)</h3>
                    <p>Você está **{pos}º** na fila para começar a rodada de escolhas.</p>
                    <p className="text-sm">O próximo a finalizar a rodada é: **{proximoDaFila?.nome || 'N/D'}** (Ordem {proximoDaFila?.ordem_antiguidade})</p>
                </div>
            );
        }
    }
    return (
      <div className="p-4 bg-gray-100 border border-gray-400 text-gray-800 rounded-lg shadow">
        <h3 className="font-bold text-lg">Status Inativo</h3>
        <p>Aguardando o início da rodada ou a finalização dos cadetes anteriores.</p>
      </div>
    );
  };

  const renderOpcoesKanban = () => {
    if (!isMinhaVezNaFila && !jaInteragiu && proximoDaFila) return <p className="text-gray-500 p-4">Aguardando a sua vez na fila para exibir as opções de escolha.</p>;
    if (opcoesDisponiveis.length === 0 && isRoundOpen) return <p className="text-gray-500">Nenhuma opção disponível ou válida no momento.</p>;

    const PCO_ELEMENT = opcoesAgrupadas.PCO_POR_DIA.size > 0 && (
      <div className="p-4 rounded-xl shadow-lg border border-sky-300 bg-sky-50">
          <h4 className="text-2xl font-bold text-sky-800 mb-4 border-b border-sky-300 pb-2">Estágios PCO (Operacional)</h4>
          <div className="flex overflow-x-auto space-x-4 pb-4">
              {[...opcoesAgrupadas.PCO_POR_DIA.entries()]
                  .sort(([, a], [, b]) => a.dataTimestamp.toDate() - b.dataTimestamp.toDate())
                  .map(([dataStr, diaGroup]) => (
                    <div key={dataStr} className="flex-shrink-0 w-80 min-w-80">
                        <div className="px-4 py-3 bg-sky-200 rounded-t-lg shadow-sm">
                            <h5 className="font-bold text-sky-900 text-md truncate">{diaGroup.dataNome}</h5>
                        </div>
                        <div className="p-2 space-y-3 bg-white border border-sky-200 rounded-b-lg shadow-md">
                          {[...diaGroup.opcoesPorCPA.entries()].map(([cpa, options]) => (
                              <div key={cpa} className="space-y-2 border-b last:border-b-0 pb-2">
                                    <p className="text-xs font-semibold text-gray-700 pt-1">{cpa}</p>
                                    {options.map(op => (
                                        <OpcaoCard key={op.id} opcao={op} handleSelecionarOpcao={handleSelecionarOpcao} todasEscolhas={todasEscolhas} />
                                    ))}
                                </div>
                            ))}
                            {!diasEmQueJaEscolhiOuPulei.has(dataStr) && (
                                <div className="pt-2 border-t border-gray-100">
                                    <Button onClick={() => handleAbrirImpossibilidadePorDia(dataStr, diaGroup.dataTimestamp)} variant="danger" className="w-full text-xs">Marcar Impossibilidade neste Dia</Button>
                                </div>
                            )}
                        </div>
                    </div>
              ))}
          </div>
      </div>
    );

    const PJ_ELEMENT = opcoesAgrupadas.PJ_POR_DIA.size > 0 && (
      <div className="p-4 rounded-xl shadow-lg border border-green-300 bg-green-50">
          <h4 className="text-2xl font-bold text-green-800 mb-4 border-b border-green-300 pb-2">Estágios PJ (Jurídica)</h4>
          <div className="flex overflow-x-auto space-x-4 pb-4">
            {[...opcoesAgrupadas.PJ_POR_DIA.entries()]
                .sort(([, a], [, b]) => a.dataTimestamp.toDate() - b.dataTimestamp.toDate())
                .map(([dataStr, diaGroup]) => (
                    <div key={dataStr} className="flex-shrink-0 w-80 min-w-80">
                        <div className="px-4 py-3 bg-green-200 rounded-t-lg shadow-sm">
                            <h5 className="font-bold text-green-900 text-md truncate">{diaGroup.dataNome}</h5>
                        </div>
                        <div className="p-2 space-y-3 bg-white border border-green-200 rounded-b-lg shadow-md">
                            {diaGroup.opcoes.map(op => (
                                <OpcaoCard key={op.id} opcao={op} handleSelecionarOpcao={handleSelecionarOpcao} todasEscolhas={todasEscolhas} />
                            ))}
                            {!diasEmQueJaEscolhiOuPulei.has(dataStr) && (
                                <div className="pt-2 border-t border-gray-100">
                                    <Button onClick={() => handleAbrirImpossibilidadePorDia(dataStr, diaGroup.dataTimestamp)} variant="danger" className="w-full text-xs">Marcar Impossibilidade neste Dia</Button>
                                </div>
                            )}
                        </div>
                    </div>
            ))}
          </div>
      </div>
    );

    return <div className="space-y-8">{PCO_ELEMENT}{PJ_ELEMENT}</div>;
  };

  const vagasParaTroca = useMemo(() => {
      const hojeStr = getNormalizedDateString(new Date());
      return datasEstagio.filter(vaga => {
          const dataVagaStr = getNormalizedDateString(vaga.data);
          if (dataVagaStr < hojeStr) return false; 
          return true;
      }).sort((a, b) => a.data.toDate() - b.data.toDate());
  }, [datasEstagio, todasEscolhas]);

  const renderFila = () => {
      let listaFiltrada = filaUnificada;
      if (filterDate || filterLocalId) {
          listaFiltrada = listaFiltrada.filter(item => {
              return item.escolhasCadete.some(e => {
                  if (e.opcao_impossibilidade || !e.local_id || !e.data) return false;
                  let matchLocal = filterLocalId ? e.local_id === filterLocalId : true;
                  let matchDate = filterDate ? getNormalizedDateString(e.data) === filterDate : true;
                  return matchLocal && matchDate;
              });
          });
      }

      return (
        <div className="space-y-4">
          <div className="p-4 bg-gray-50 rounded-lg shadow-sm border border-gray-200">
     
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                      <select value={filterLocalId} onChange={(e) => setFilterLocalId(e.target.value)} className="w-full border p-2 rounded">
                          <option value="">-- Todos os Locais --</option>
                          {locais.map(l => <option key={l.id} value={l.id}>{l.nome}</option>)}
                      </select>
                  </div>
                  <div>
                      <input type="date" value={filterDate} onChange={(e) => setFilterDate(e.target.value)} className="w-full border p-2 rounded" />
                  </div>
                  <div className="flex items-end gap-2 h-full">
                      <Button 
                          onClick={() => { setFilterLocalId(''); setFilterDate(''); }} 
                          variant="outline" 
                          className="flex-1 h-10 text-sm font-medium flex items-center justify-center"
                      >
                          Limpar
                      </Button>
                      <Button 
                          onClick={() => setShowAlocarExternoModal(true)} 
                          variant="primary" 
                          className="flex-1 h-10 text-sm font-medium flex items-center justify-center bg-purple-600 hover:bg-purple-700 border-purple-600"
                      >
                          + Cadete
                      </Button>

                      <Button
                      onClick={() => setShowPrintDateModal(true)} 
                      variant="primary">
                      Gerar Escala
                      </Button>

                      
                  </div>
              </div>
          </div>

          <div className="bg-white shadow rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-16">Ordem</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/4">Nome / Origem</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/6">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-1/2">Local / Dia / Ações</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {listaFiltrada.map((item, index) => {
                    const isExterno = item.tipo_vinculo === 'EXTERNO';
                    
                    const renderDivisor = isExterno && (index === 0 || listaFiltrada[index - 1].tipo_vinculo !== 'EXTERNO');

                    let statusText = '';
                    
                    if (isExterno) {
                        statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800">Alocado</span>;
                    } else {
                        const pulouGeral = item.escolhasCadete.find(e => e.opcao_impossibilidade && !e.data);
                        const isNext = item.id === proximoDaFila?.id;
                        const isComplete = cadetesCompletaramRodada.get(item.id);
                        const isSkipped = item.skip_queue;

                        if (isSkipped) statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 border border-yellow-300">PULAR (Escalante)</span>;
                        else if (pulouGeral) statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-orange-100 text-orange-800">Impossib. Geral</span>;
                        else if (isNext && !(filterDate || filterLocalId)) statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">Próximo a Escolher</span>;
                        else if (isComplete && !(filterDate || filterLocalId)) statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">Rodada Concluída</span>;
                        else statusText = <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800">Aguardando</span>;
                    }

                    return (
                        <React.Fragment key={item.id}>
                            {renderDivisor && (
                                <tr className="bg-purple-100">
                                    <td colSpan="4" className="px-6 py-3 text-center text-sm font-bold text-purple-800 uppercase tracking-widest border-y border-purple-200 shadow-inner">
                                        Cadetes Alocados de Outros Pelotões
                                    </td>
                                </tr>
                            )}
                            
                            <tr className={`${(item.id === proximoDaFila?.id && !item.skip_queue && !isExterno && !(filterDate || filterLocalId)) ? 'bg-blue-50' : ''} ${isExterno ? 'bg-purple-50 border-l-4 border-purple-400' : ''}`}>
                            <td className="px-4 py-4 text-sm font-bold text-gray-900">{item.ordem_antiguidade}</td>
                            <td className="px-4 py-4 text-sm text-gray-700">
                                {item.nome}
                                {item.id === userProfile.id && <span className="ml-2 text-xs text-blue-600 font-bold">(Você)</span>}
                                {isExterno && <span className="ml-2 text-[10px] text-purple-700 font-bold border border-purple-300 bg-purple-100 px-1 rounded">EXTERNO ({item.pelotao_origem})</span>}
                            </td>
                            <td className="px-4 py-4 whitespace-nowrap text-sm">{statusText}</td>
                            
                            <td className="px-4 py-4 text-sm text-gray-500 align-middle">
                                {item.escolhasCadete.length === 0 ? (
                                    <span className="text-xs text-gray-400 italic">Nenhuma alocação</span>
                                ) : (
                                    <div className="border border-gray-200 rounded-md bg-white shadow-sm overflow-hidden">
                                        <button
                                            onClick={() => toggleCadeteAccordion(item.id)}
                                            className="w-full flex justify-between items-center px-3 py-2 bg-gray-50 hover:bg-gray-100 transition-colors focus:outline-none"
                                        >
                                            <span className="text-xs font-bold text-gray-600 uppercase tracking-wider">
                                                {(() => {
                                                    const qtdImp = item.escolhasCadete.filter(e => e.opcao_impossibilidade).length;
                                                    const qtdEst = item.escolhasCadete.length - qtdImp;
                                                    let txt = [];
                                                    if (qtdEst > 0) txt.push(`${qtdEst} ${qtdEst === 1 ? 'Estágio' : 'Estágios'}`);
                                                    if (qtdImp > 0) txt.push(`${qtdImp} ${qtdImp === 1 ? 'Impossibilidade' : 'Impossibilidades'}`);
                                                    return txt.join(' | ');
                                                })()}
                                            </span>
                                            <svg 
                                                className={`w-4 h-4 text-gray-500 transform transition-transform duration-200 ${expandedCadetes[item.id] ? 'rotate-180' : ''}`} 
                                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                        
                                        {expandedCadetes[item.id] && (
                                            <div className="px-3 pb-1 border-t border-gray-100 bg-white">
                                                {item.escolhasCadete.map(e => {
                                                    const dataEscolhaStr = e.data ? getNormalizedDateString(e.data) : '';
                                                    const hojeStr = getNormalizedDateString(new Date());
                                                    
                                                    // Trava de edição: Você só edita vagas que são do SEU pelotão
                                                    const isMinhaVaga = e.pelotao_id === userProfile.pelotao_id;
                                                    const canEdit = dataEscolhaStr >= hojeStr && isMinhaVaga;

                                                    return (
                                                        <div key={e.id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0 gap-4">
                                                            <span className={`flex-1 ${e.opcao_impossibilidade ? 'text-orange-600' : 'text-gray-700'} ${e.tipo === 'EXTERNO' ? 'text-purple-800 font-medium' : ''}`}>
                                                                
                                                                {/* TRATAMENTO VISUAL: IMPOSSIBILIDADE VS ESTÁGIO */}
                                                                {e.opcao_impossibilidade ? (
                                                                    <>
                                                                        {e.data ? formatarData(e.data) : 'GERAL'} - <span className="font-bold uppercase">Impossibilidade</span>
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        {formatarData(e.data)} - {e.local_nome} ({e.tipo}) 
                                                                        {e.turno && <span className="ml-1 text-xs font-semibold text-blue-600">[{e.turno.toUpperCase()}]</span>}
                                                                    </>
                                                                )}
                                                                
                                                                {/* TAG VISUAL PARA VAGAS DOS OUTROS PELOTÕES */}
                                                                {e.tipo === 'EXTERNO' && !isMinhaVaga && (
                                                                    <span className="ml-2 text-[10px] font-bold text-purple-600 bg-purple-100 px-1.5 py-0.5 rounded border border-purple-200 uppercase tracking-widest">
                                                                        Alocado por outro pelotão
                                                                    </span>
                                                                )}
                                                            </span>
                                                            
                                                            {canEdit && (
                                                                <Button 
                                                                    onClick={() => handleOpenGerenciarEscolha(e)} 
                                                                    className="w-20 py-1 px-0 text-[10px] font-semibold flex justify-center items-center shadow-sm rounded bg-gray-600 hover:bg-gray-700 text-white border-gray-600"
                                                                    title="Alterar, Impossibilidade ou Excluir"
                                                                >
                                                                    <EditPencilIcon /> <span className="ml-1">Editar</span>
                                                                </Button>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </td>
                            </tr>
                        </React.Fragment>
                    );
                })}
                {filaUnificada.length === 0 && (filterDate || filterLocalId) && (<tr><td colSpan="4" className="text-center py-4 text-gray-500">Nenhum cadete encontrado com este filtro.</td></tr>)}
              </tbody>
            </table>
          </div>
          
        </div>
      );
  };

  const renderAssinaturas = () => (
      <div className="p-4 bg-white rounded-lg shadow max-w-xl mx-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Configuração de Assinaturas (PDF)</h3>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
            <input name="id" type="hidden" value={pelotaoId} />
            <h4 className="text-lg font-medium border-b pb-2">Assinatura do Escalante (Esquerda)</h4>
            <div>
                <label>Nome Completo do Escalante</label>
                <input name="nome_escalante" value={formData.nome_escalante || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
            </div>
            <div>
                <label>Posto/Graduação do Escalante</label>
                <input name="posto_escalante" value={formData.posto_escalante || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
            </div>

            <h4 className="text-lg font-medium border-b pb-2 pt-4">Assinatura do Comandante de Pelotão (Direita)</h4>
            <div>
                <label>Nome Completo do Cmt de Pelotão</label>
                <input name="nome_cmt_pelotao" value={formData.nome_cmt_pelotao || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
            </div>
            <div>
                <label>Posto e função do Resp. Assinatura</label>
                <input name="posto_cmt_pelotao" value={formData.posto_cmt_pelotao || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" />
            </div>
            
            <div className="pt-4 border-t">
                {error && <p className="text-red-600 mb-2">{error}</p>}
                <Button type="submit" variant="primary" disabled={processing} className="w-full">
                    {processing ? "Salvando..." : "Salvar Assinaturas"}
                </Button>
            </div>
        </form>
      </div>
  );

  const renderCRUD = (title, pathKey, columns, items) => {
      let itemsFiltered = items;
      const hojeStr = getNormalizedDateString(new Date());

      if (pathKey === 'dataEstagio') {
          itemsFiltered = items.filter(i => getNormalizedDateString(i.data) >= hojeStr); 
          itemsFiltered.sort((a, b) => a.data.toDate() - b.data.toDate());
      }

      const primeiroCadete = cadetes.reduce((prev, curr) => (prev.ordem_antiguidade < curr.ordem_antiguidade ? prev : curr), cadetes[0]);
      const futureStageIds = new Set(itemsFiltered.map(i => i.id));
      let travaEscala = false;

      if (pathKey === 'dataEstagio' && itemsFiltered.length > 0 && primeiroCadete) {
          const primeiroComecou = escolhas.some(e => 
              e.cadete_id === primeiroCadete.id && (
                  (e.opcao_impossibilidade && !e.data) || 
                  (e.data && futureStageIds.has(e.data_estagio_id))
              )
          );
          if (primeiroComecou) {
              travaEscala = true;
          }
      }

      const isButtonLocked = pathKey === 'dataEstagio' && isFilaLiberada;

      return (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-semibold">{title}</h3>
            <div className="space-x-2">
                {pathKey === 'dataEstagio' && <Button onClick={() => handleOpenModal('deleteBulk')} variant="danger">Excluir Datas (em Bloco)</Button>}
                
                {pathKey !== 'cadete' && (
                    <Button 
                        onClick={() => {
                            if (isButtonLocked) {
                                alert("A fila de escolhas está LIGADA. Para criar novas vagas, você deve primeiro PAUSAR a rodada no topo da página.");
                                return;
                            }
                            if (travaEscala && !isButtonLocked) {
                                if (!window.confirm(`ATENÇÃO: A fila de escolhas é única.\n\nObserve se o Cadete ${primeiroCadete?.nome} iniciou a fila antes de gerar.\n\nAdicionar novos estágios após ter liberado a rodada reiniciará a fila automaticamente.\n\nDeseja continuar?`)) {
                                    return;
                                }
                            }
                            handleOpenModal(pathKey);
                        }} 
                        variant="success"
                        disabled={isButtonLocked}
                        title={isButtonLocked ? "PAUSE a rodada para criar novas vagas" : "Criar Novo Item"}
                        className={isButtonLocked ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed border-gray-400" : ""}
                    >
                        {isButtonLocked ? "Bloqueado (Fila ON)" : "Novo"}
                    </Button>
                )}
            </div>
          </div>
          
          {pathKey === 'dataEstagio' && isFilaLiberada && (
              <div className="mb-4 p-3 bg-blue-50 border-l-4 border-blue-400 text-blue-800 text-sm">
                  <p className="font-bold">ℹ️ Modo de Execução</p>
                  <p>A rodada de escolhas está em andamento. A criação de novas vagas está bloqueada para evitar conflitos. Aguarde todos escolherem para criar novas datas de estágio.</p>
              </div>
          )}
          
          {pathKey === 'dataEstagio' && <p className="mb-3 text-xs text-gray-500 italic bg-gray-50 p-1 rounded inline-block border">* Vagas de dias passados (ontem ou antes) são ocultadas automaticamente desta lista.</p>}

          {globalError && ( 
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">
              <strong>Erro:</strong> {globalError}
              <button onClick={() => setGlobalError('')} className="ml-4 font-bold float-right">&times;</button>
            </div>
          )}

          <div className="bg-white shadow rounded-lg overflow-x-auto max-h-[600px]">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>{columns.map(col => <th key={col.key} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{col.label}</th>)}<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th></tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {itemsFiltered.map(item => (
                  <tr key={item.id}>
                    {columns.map(col => (
                      <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">{col.render ? col.render(item[col.key], item) : item[col.key]}</td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2 flex items-center justify-end">
                      
                      {pathKey === 'cadete' ? (
                          <>
                              <button
                                  onClick={() => handleToggleSkipQueue(item)}
                                  className={`relative inline-flex items-center h-7 w-24 rounded-full transition-colors duration-300 focus:outline-none shadow-sm px-0.5 ${
                                      item.skip_queue 
                                      ? "bg-red-500 flex-row-reverse justify-between" 
                                      : "bg-green-500 justify-between"
                                  }`}
                                  title={item.skip_queue 
                                      ? "O cadete está pular. Clique para RETORNAR este cadete à fila de escolhas." 
                                      : "O cadete está ativo. Clique para fazer o sistema PULAR esta pessoa durante a fila das escolhas."
                                  }
                              >
                                  <span className={`text-[10px] font-bold text-white uppercase ${item.skip_queue ? "mr-2" : "ml-2"}`}>
                                      {item.skip_queue ? 'Pular' : 'Ativo'}
                                  </span>
                                  <span className="inline-block h-6 w-6 transform rounded-full bg-white shadow flex items-center justify-center">
                                      <span className={item.skip_queue ? "text-red-600" : "text-green-600"}>
                                          {item.skip_queue ? <UserSkippedIcon /> : <UserActiveIcon />}
                                      </span>
                                  </span>
                              </button>
                              
                              <button
                                  onClick={() => handleToggleRepeatLocal(item)}
                                  className={`relative inline-flex items-center h-7 w-28 rounded-full transition-colors duration-300 focus:outline-none shadow-sm px-0.5 ml-2 ${
                                      item.allow_repeat_local 
                                      ? "bg-purple-500 flex-row-reverse justify-between" 
                                      : "bg-gray-300 justify-between"
                                  }`} 
                                  title={item.allow_repeat_local
                                      ? "O cadete pode repetir local. Clique para BLOQUEAR a repetição."
                                      : "O cadete NÃO pode repetir local. Clique para PERMITIR a repetição."
                                  }
                              >
                                  <span className={`text-[10px] font-bold text-white uppercase ${item.allow_repeat_local ? "mr-2" : "ml-2"}`}>
                                      {item.allow_repeat_local ? 'Repetir ON' : 'Repetir OFF'}
                                  </span>
                                  <span className="inline-block h-6 w-6 transform rounded-full bg-white shadow flex items-center justify-center">
                                      <span className={item.allow_repeat_local ? "text-purple-600" : "text-gray-600"}>
                                          {item.allow_repeat_local ? <UserActiveIcon /> : <UserSkippedIcon />}
                                      </span>
                                  </span>
                              </button>

                              <Button 
                                  onClick={() => handleResetPasswordCadete(item.id, item.cpf, item.nome)} 
                                  variant="outline" 
                                  className="text-yellow-600 border-yellow-200 hover:bg-yellow-50 flex items-center gap-1 ml-2"
                                  title={`Resetar senha de ${item.nome} para o CPF`}
                              >
                                  <KeyIcon /> Resetar Senha
                              </Button>
                          </>
                      ) : (
                          <>
                              <Button onClick={() => handleOpenModal(pathKey, item)} variant="outline" disabled={pathKey === 'dataEstagio'}>
                                {pathKey === 'dataEstagio' ? 'N/A' : 'Editar'}
                              </Button>
                              <Button onClick={() => confirmDelete(pathKey, item.id)} variant="danger" disabled={item.id === userProfile.id}>Excluir</Button>
                          </>
                      )}

                    </td>
                  </tr>
                ))}
                {itemsFiltered.length === 0 && <tr><td colSpan={columns.length + 1} className="text-center py-4 text-gray-500">Nenhum item futuro ou disponível encontrado.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      );
  };

  const cadeteCols = [{ key: 'ordem_antiguidade', label: 'Ordem' }, { key: 'nome', label: 'Nome' }, { key: 'matricula', label: 'RE' }, { key: 'cpf', label: 'CPF' }, { key: 'telefone', label: 'Celular' }];
  const dataEstagioCols = [{ key: 'data', label: 'Data', render: (ts) => formatarData(ts) }, { key: 'local_id', label: 'Local', render: (id) => locais.find(l => l.id === id)?.nome || 'N/D' }, { key: 'tipo', label: 'Tipo' }, { key: 'turno', label: 'Turno' }, { key: 'capacidade', label: 'Capac.' }, { key: 'pelotao_id', label: 'Pelotão', render: (id) => id ? (locais.find(l => l.id === id)?.nome || (id === pelotaoId ? userProfile.pelotao_nome : 'Outro')) : 'Global' }];

  const renderModalContent = () => {
    if (processing) return <ModalLoadingSpinner text="Salvando..." />;

    switch (modalView) {
      case 'cadete':
        const isCadeteInPelotao = ['cadete', 'escalante', 'cadete_academico'].includes(currentItem?.nivel);
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <input name="id" type="hidden" value={formData.id || ''} />
            <div><label>Nome</label><input name="nome" value={formData.nome || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" /></div>
            <div><label>CPF (só números)</label><input name="cpf" value={formData.cpf || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" placeholder="11122233344" />{!currentItem && <p className="text-xs text-gray-500 mt-1">A senha inicial será o CPF.</p>}</div>
            <div><label>RE (Registro Estatístico)</label><input name="matricula" value={formData.matricula || ''} onChange={handleFormChange} className="w-full border p-2 rounded" /></div>
            <div><label>Ordem de Antiguidade (1 a 35)</label><input name="ordem_antiguidade" type="number" value={formData.ordem_antiguidade || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" /></div>
            <div><label>Celular (Opcional, somente números)</label><input name="telefone" value={formData.telefone || ''} onChange={handleFormChange} className="w-full border p-2 rounded" placeholder="11999998888" maxLength="11" /></div>
            {error && <div className={`p-2 rounded-md mt-2 ${error.startsWith('Sucesso!') ? 'bg-green-100 text-green-700 border border-green-400' : 'bg-red-100 text-red-600 border border-red-400'}`}>{error}</div>}
            <div className="flex justify-between gap-3 pt-4 border-t"> 
              {currentItem && isCadeteInPelotao && <Button type="button" onClick={() => handleResetPasswordCadete(currentItem.id, formData.cpf, formData.nome)} variant="secondary" className="bg-yellow-500 text-gray-900 hover:bg-yellow-600 focus:ring-yellow-500 shadow-md" disabled={processing}>Resetar Senha</Button>}
              <div className="flex gap-3 ml-auto"><Button onClick={() => setShowModal(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary" disabled={processing}>Salvar</Button></div>
            </div>
          </form>
        );

      case 'dataEstagio':
        const diaSelecionado = formData.data ? getDiaDaSemana(formData.data) : -1;
        const isPJ = formData.tipo === 'PJ';
        return (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><label>1. Tipo de Estágio</label><select name="tipo" value={formData.tipo || ''} onChange={handleFormChange} required className="w-full border p-2 rounded"><option value="">Selecione o Tipo...</option><option value="PCO">PCO (Operacional)</option><option value="PJ">PJ (Jurídica)</option></select></div>
            <div><label>2. Data</label><input name="data" type="date" value={formData.data || ''} onChange={handleFormChange} required className="w-full border p-2 rounded" /></div>
            <p className="text-xs text-blue-700 bg-blue-50 p-2 rounded-md">Nota: As vagas criadas aqui serão visíveis **apenas para o seu pelotão ({userProfile.pelotao_nome})**.</p>
            {!isPJ && (<div><label className="block text-sm font-medium text-gray-700 mb-2">3. Turnos (PCO)</label><div className="space-y-2 p-3 bg-gray-50 rounded-md border"><label className="flex items-center"><input type="checkbox" name="pcoDia" checked={formData.pcoDia || false} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/><span className="ml-2 text-gray-700">Criar Vagas de DIA (Capacidade 1)</span></label><label className="flex items-center"><input type="checkbox" name="pcoNoite" checked={formData.pcoNoite || false} onChange={handleFormChange} className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"/><span className="ml-2 text-gray-700">Criar Vagas de NOITE (Capacidade 1)</span></label></div></div>)}
            {isPJ && (<><div><label>3. Modo de Criação (PJ)</label><select name="creationMode" value={formData.creationMode || 'auto'} onChange={handleFormChange} className="w-full border p-2 rounded bg-gray-50"><option value="auto">Automático (Baseado no Expediente)</option><option value="manual">Manual (Definir Vagas)</option></select></div>{formData.creationMode === 'manual' && (<div className="w-full border-2 border-yellow-300 p-3 rounded bg-yellow-50 text-yellow-900 space-y-3"><h4 className="font-semibold text-center">Definição Manual de Vagas</h4><div className="grid grid-cols-2 gap-4"><div><label className="block text-sm font-medium">Vagas (DIA)</label><input name="manualVagasDia" type="number" value={formData.manualVagasDia || 0} onChange={handleFormChange} min="0" className="w-full border p-2 rounded" /></div><div><label className="block text-sm font-medium">Vagas (NOITE)</label><input name="manualVagasNoite" type="number" value={formData.manualVagasNoite || 0} onChange={handleFormChange} min="0" className="w-full border p-2 rounded" /></div></div></div>)}{formData.creationMode === 'auto' && (<div className="w-full border-2 border-blue-300 p-3 rounded bg-blue-50 text-blue-900 space-y-3"><h4 className="font-semibold text-center">Definição Automática (PJ)</h4>{diaSelecionado === -1 && <p className="text-center">Selecione uma data para ver as opções.</p>}{diaSelecionado >= 0 && diaSelecionado <= 4 && (<div><label className="block font-medium mb-1">Haverá expediente escolar no dia SEGUINTE?</label><select name="pjNextDayExpediente" value={formData.pjNextDayExpediente || 'nao'} onChange={handleFormChange} className="w-full border p-2 rounded"><option value="sim">Sim (Cria 2 Dia, 1 Noite)</option><option value="nao">Não (Cria 3 Dia)</option></select></div>)}{diaSelecionado === 5 && (<div><label className="block font-medium mb-1">Haverá expediente escolar neste dia (Sexta-feira)?</label><select name="pjFridayExpediente" value={formData.pjFridayExpediente || 'sim'} onChange={handleFormChange} className="w-full border p-2 rounded"><option value="sim">Sim (Cria 3 Noite)</option><option value="nao">Não (Cria 2 Dia, 1 Noite)</option></select></div>)}{diaSelecionado === 6 && (<p className="font-medium text-center p-2 bg-blue-100 rounded">Sábado: Serão criados turnos DIA (2 VAGAS) e NOITE (1 VAGA) por local.</p>)}</div>)}</>)}
            {error && <p className="text-red-600">{error}</p>}
            <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModal(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button type="submit" variant="primary">Criar Vagas</Button></div>
          </form>
        );

      case 'deleteBulk': 
        const datasComEstagio = Array.from(new Set(datasEstagio.filter(d => d.pelotao_id === null || d.pelotao_id === userProfile.pelotao_id).map(d => getNormalizedDateString(d.data)))).sort(); 
        return (
            <form onSubmit={(e) => { e.preventDefault(); confirmDelete('datasEstagio', null, deleteBulkDate); }} className="space-y-4">
              <p className="text-gray-700">Selecione o dia que deseja excluir **TODAS** as datas de estágio (PCO e PJ) criadas por você.</p>
              <p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded-md">Atenção: Vagas globais (criadas pelo Admin) não serão afetadas.</p>
              <p className="text-sm text-red-600 font-semibold">A exclusão só será possível se não houver NENHUMA escolha de cadete associada a estas datas.</p>
              <div><label>Dia para Excluir em Bloco (Apenas vagas do {userProfile.pelotao_nome})</label><select name="deleteDate" value={deleteBulkDate} onChange={(e) => setDeleteBulkDate(e.target.value)} required className="w-full border p-2 rounded"><option value="">Selecione a Data...</option>{datasComEstagio.map(dateStr => (<option key={dateStr} value={dateStr}>{formatarData(new Date(dateStr + 'T12:00:00Z'))}</option>))}</select></div>
              {error && <p className="text-red-600">{error}</p>}
              <div className="flex justify-end gap-3 pt-4 border-t"><Button onClick={() => setShowModal(false)} variant="secondary">Cancelar</Button><Button type="submit" variant="danger" disabled={processing || !deleteBulkDate}>{processing ? "Excluindo..." : "Excluir em Bloco"}</Button></div>
            </form>
        );
        
      case 'assinaturas': 
        return renderAssinaturas();

      default: return null;
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div>
      {/* HEADER COM TOGGLE SWITCH */}
      <header className="flex justify-between items-center mb-6 pb-4 border-b">
        <div className="flex items-center space-x-4">
          <img src="https://apmbb.com.br/resultados/brasao.png" alt="Brasão APMBB" className="h-12 w-12" />
          <div>
          <h1 className="text-3xl font-bold text-gray-800">Escalante de Operações</h1>
          <p className="text-gray-600">Bem vindo {userProfile.nome} - Turma / Pelotão: {userProfile.pelotao_nome || 'N/D'} </p>
        </div>
        </div>
        
        {/* --- INTERRUPTOR --- */}
        <div className="flex items-center gap-3 bg-white p-3 rounded-xl shadow-sm border border-gray-200">
            <div className="flex flex-col items-end">
                <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Status da Rodada</span>
                <span className={`text-sm font-bold ${isFilaLiberada ? 'text-green-600' : 'text-red-600'}`}>
                    {isFilaLiberada ? 'FILA LIBERADA (ON)' : 'PREPARAÇÃO (OFF)'}
                </span>
            </div>
            <button 
                onClick={handleToggleFila}
                className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                    isFilaLiberada ? 'bg-green-500' : 'bg-gray-300'
                }`}
            >
                <span className="sr-only">Liberar Escolhas</span>
                <span
                    className={`inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-300 ease-in-out ${
                        isFilaLiberada ? 'translate-x-7' : 'translate-x-1'
                    }`}
                />
            </button>
        </div>
      </header>

      {globalError && <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg"><strong>Erro:</strong> {globalError}<button onClick={() => setGlobalError('')} className="ml-4 font-bold float-right">&times;</button></div>}

      {datasEstagio.length === 0 && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg">
              <h3 className="font-bold">Atenção</h3>
              <p>Não há datas de estágio cadastradas para o pelotão. Configure as datas na aba "Datas Estágio" para iniciar a rodada.</p>
          </div>
      )}

      {/* --- NOVO ESTILO DE ABAS (Cápsula Moderna) --- */}
      <div className="flex justify-start mb-8">
        <div className="flex space-x-2 bg-white p-2 rounded-2xl shadow-sm border border-gray-200">
            <button onClick={() => setActiveTab('fila')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'fila' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}>
              <QueueListIcon /> Fila (Tempo Real)
            </button>
            <button onClick={() => setActiveTab('cadetes')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'cadetes' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}>
              <UserGroupIcon /> Cadetes
            </button>
            <button onClick={() => setActiveTab('datas')} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'datas' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}>
              <CalendarIcon /> Datas de Estágio
            </button>
            <button onClick={() => { setActiveTab('assinaturas'); handleOpenModal('assinaturas'); }} className={`flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl font-semibold text-sm transition-all duration-200 ease-in-out ${activeTab === 'assinaturas' ? 'bg-blue-600 text-white shadow-md transform scale-105' : 'text-gray-600 hover:bg-gray-100'}`}>
              <PencilIcon /> Assinaturas
            </button>
        </div>
      </div>

      <main>
        {activeTab === 'fila' && renderFila()}
        {activeTab === 'cadetes' && renderCRUD('Relação dos cadetes do pelotão', 'cadete', cadeteCols, cadetes.filter(c => c.id !== userProfile.id))}
        {activeTab === 'datas' && renderCRUD('Gerenciar Datas de Estágio', 'dataEstagio', dataEstagioCols, datasEstagio.filter(d => d.pelotao_id === null || d.pelotao_id === userProfile.pelotao_id))}
        {activeTab === 'assinaturas' && renderAssinaturas()}
      </main>

      {/* --- MODAL GERENCIAR ESCOLHA (NOVO) --- */}
      <Modal show={showGerenciarEscolhaModal} onClose={() => setShowGerenciarEscolhaModal(false)} title="Gerenciar Escolha do Cadete" size="md">
          {escolhaEmEdicao && (
              <div className="space-y-6">
                  <div className="bg-gray-50 p-3 rounded border">
                      <p className="text-sm font-bold text-gray-700">Cadete: <span className="font-normal">{escolhaEmEdicao.cadete_nome}</span></p>
                      <p className="text-sm font-bold text-gray-700">Atual: <span className="font-normal">{formatarData(escolhaEmEdicao.data)} - {escolhaEmEdicao.local_nome} ({escolhaEmEdicao.tipo}/{escolhaEmEdicao.turno})</span></p>
                  </div>

                  <div className="border-t pt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Trocar para outra vaga disponível:</label>
                      <div className="flex gap-2">
                          <select 
                              className="w-full border p-2 rounded text-sm"
                              value={novaVagaId}
                              onChange={(e) => setNovaVagaId(e.target.value)}
                          >
                              <option value="">-- Selecione uma nova vaga --</option>
                              {vagasParaTroca.map(v => (
                                  <option key={v.id} value={v.id}>
                                      {formatarData(v.data)} - {v.local_nome} ({v.tipo}/{v.turno})
                                  </option>
                              ))}
                          </select>
                          <Button 
                              onClick={handleTrocarEstagio} 
                              variant="success" 
                              disabled={!novaVagaId || processing}
                              className="whitespace-nowrap"
                          >
                              Trocar
                          </Button>
                      </div>
                  </div>

                  <div className="border-t pt-4 grid grid-cols-2 gap-4">
                      <div>
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-2">Opções de Cancelamento</label>
                          <Button 
                              onClick={() => {
                                  setShowGerenciarEscolhaModal(false);
                                  setEscolhaParaCancelar(escolhaEmEdicao);
                                  setShowModalCancelamento(true);
                              }}
                              variant="secondary"
                              className="w-full bg-yellow-500 hover:bg-yellow-600 text-white border-yellow-600 text-xs"
                          >
                              Marcar Impossibilidade
                          </Button>
                      </div>
                      <div className="flex items-end">
                          <Button 
                              onClick={() => {
                                  if(window.confirm("Tem certeza que deseja excluir esta escolha? O cadete voltará para a fila.")) {
                                      confirmDelete('escolha', escolhaEmEdicao.id);
                                  }
                              }}
                              variant="danger"
                              className="w-full text-xs"
                          >
                              Excluir Escolha
                          </Button>
                      </div>
                  </div>
                  
                  {error && <p className="text-red-600 text-sm">{error}</p>}
              </div>
          )}
      </Modal>

      {/* Modais */}
      <Modal show={showModal} onClose={() => setShowModal(false)} title={modalView === 'deleteBulk' ? 'Excluir Datas de Estágio em Bloco' : (modalView === 'dataEstagio' ? 'Criar Novas Vagas de Estágio' : (currentItem ? 'Editar Item' : 'Novo Item'))} size={modalView === 'dataEstagio' ? "md" : (modalView === 'assinaturas' ? "sm" : "lg")}>
        {renderModalContent()}
      </Modal>

      <Modal show={showConfirm} onClose={() => setShowConfirm(false)} title="Confirmar Exclusão" size="sm">
        <p>Tem certeza que deseja excluir este item? Esta ação não pode ser desfeita.</p>
        {deleteAction?.itemType === 'escolha' && (<p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded-md my-3">Atenção: A exclusão de uma **Escolha** fará com que o cadete voltará para o estado de "Aguardando Escolha" e seja re-enquadrado na fila de acordo com a ordem de antiguidade do pelotão.</p>)}
        {globalError && <p className="text-red-600 mt-2">{globalError}</p>}
        <div className="flex justify-end gap-3 mt-4"><Button onClick={() => { setShowConfirm(false); setGlobalError(''); }} variant="secondary">Cancelar</Button><Button onClick={handleDelete} variant="danger">Excluir</Button></div>
      </Modal>

      <ChangePasswordModal show={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} userProfile={userProfile} />

      <Modal show={showModalEscolha} onClose={() => !processing && setShowModalEscolha(false)} title="Confirmar Escolha">
        {selectedEstagio && (
          <div>
            <p className="text-gray-700">Você está prestes a confirmar sua escolha:</p>
            <div className="my-4 p-3 bg-gray-50 rounded-lg border"><p><strong>Local:</strong> {selectedEstagio.local_nome}</p><p><strong>Data:</strong> {formatarData(selectedEstagio.data)}</p><p><strong>Tipo:</strong> {selectedEstagio.tipo} ({selectedEstagio.turno})</p></div>
            <p className="font-semibold">Esta ação não pode ser desfeita.</p>
            {error && <p className="text-red-600 mt-2">{error}</p>}
            <div className="flex justify-end gap-3 mt-4"><Button onClick={() => setShowModalEscolha(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button onClick={handleConfirmarEscolha} variant="success" disabled={processing}>{processing ? "Processando..." : "Confirmar Escolha"}</Button></div>
          </div>
        )}
      </Modal>

      <Modal show={showModalImpossibilidade} onClose={() => !processing && setShowModalImpossibilidade(false)} title={`Marcar Impossibilidade ${selectedDateToSkip ? `(Dia: ${formatarData(selectedDateToSkip.dateTimestamp)})` : '(ERRO)'}`}>
        <div>
          {selectedDateToSkip ? (<><p className="text-gray-700">Você tem certeza que deseja marcar impossibilidade para o dia **{formatarData(selectedDateToSkip.dateTimestamp)}**?</p><p className="text-sm text-yellow-700 bg-yellow-50 p-2 rounded-md my-3">Atenção: A marcação de Impossibilidade bloqueará você de escolher **apenas este dia**. Você poderá escolher outros dias nesta rodada.</p></>) : (<p className="text-red-600">Erro interno: O botão de impossibilidade geral foi removido. Feche e tente novamente.</p>)}
          <div className="mt-4"><label htmlFor="motivo" className="block text-sm font-medium text-gray-700">Motivo (Opcional)</label><textarea id="motivo" rows="3" value={motivoImpossibilidade} onChange={(e) => setMotivoImpossibilidade(e.target.value)} className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder="Ex: Atestado médico, dispensa, etc."></textarea></div>
          {error && <p className="text-red-600 mt-2">{error}</p>}
          <div className="flex justify-end gap-3 mt-4"><Button onClick={() => setShowModalImpossibilidade(false)} variant="secondary" disabled={processing}>Cancelar</Button><Button onClick={handleConfirmarImpossibilidade} variant="danger" disabled={processing || !selectedDateToSkip}>{processing ? "Salvando..." : "Confirmar Impossibilidade"}</Button></div>
        </div>
      </Modal>
      
      <CancelamentoEscolhaModal show={showModalCancelamento} onClose={() => setShowModalCancelamento(false)} escolha={escolhaParaCancelar} handleConfirmarCancelamento={handleConfirmarCancelamento} />
      <PrintDateModal 
        show={showPrintDateModal} 
        onClose={() => setShowPrintDateModal(false)} 
        availableDates={datasParaRelatorio} 
        handlePrint={handlePrintEscala} 
        cadetesExternos={cadetesExternos} 
        handleAddExternalCadete={handleAddExternalCadete} 
        />
      
      <AlocarCadeteExternoModal 
        show={showAlocarExternoModal} 
        onClose={() => setShowAlocarExternoModal(false)} 
        onConfirm={handleConfirmarAlocacaoExterna} 
        stages={datasEstagio} 
        todasEscolhas={todasEscolhas} 
        processing={processing} 
        pelotoes={allPelotoes} 
      />
    </div>
  );
};